import {
  tryVerifyAddress,
  tryFetchBalance,
  tryFetchFee,
  tryFetchAllTransactions,
} from '@/core/presenter/controller/HomeController';
import { HomeViewType } from '@/core/presenter/viewModel';
import { useStores } from '@/stores';
import dayjs from 'dayjs';
import { useState, useEffect, useCallback, useRef } from 'react';
import * as ViewModel from '@/core/presenter/viewModel';
import { RouterPath } from '@/routes/RouterPath';
import { useNavigate, useLocation } from 'react-router-dom';
import jsQR from 'jsqr';
import { MAX_MEMO_LENGTH, MIN_SOL_BALANCE } from '@/consts';
import { checkSessionAndRedirect } from '@/core/presenter/controller/LoginController';

export default function useHome() {
  const { presentationStore, userStore } = useStores();
  const [viewType, setViewType] = useState<HomeViewType>(HomeViewType.MAIN);
  const [sendAddress, setSendAddress] = useState<string>('');
  const [sendAsset, setSendAsset] = useState<string>('');
  const [selectedAsset, setSelectedAsset] = useState<ViewModel.IAsset | undefined>(userStore.myAsset?.tokenAsset);
  const [isFeeNotice, setIsFeeNotice] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(ViewModel.HomeTabType.ASSETS);
  const [memo, setMemo] = useState<string>('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [isInvalidAddressModalOpen, setIsInvalidAddressModalOpen] = useState<boolean>(false);
  const [isSendCompleteModalOpen, setIsSendCompleteModalOpen] = useState<boolean>(false);
  const [isFeeNoticeModalOpen, setIsFeeNoticeModalOpen] = useState<boolean>(false);
  const [isBalanceNotEnoughModalOpen, setIsBalanceNotEnoughModalOpen] = useState<boolean>(false);
  const [isTransactionErrorModalOpen, setIsTransactionErrorModalOpen] = useState<boolean>(false);
  const [transactionErrorMessage, setTransactionErrorMessage] = useState<string>('');
  const [isClipboardErrorModalOpen, setIsClipboardErrorModalOpen] = useState<boolean>(false);
  const hasHandledSendView = useRef<boolean>(false);
  const activities = [...presentationStore.transactions].sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix());
  const isNotActivities = activities.length < 1;
  const isWritting = sendAddress.length > 0;
  const isSendingAsset =
    (presentationStore.balance?.coinBalance ?? 0) > 0 || (presentationStore.balance?.tokenBalance ?? 0) > 0;
  const defaultMemo = presentationStore.transactions.filter((item) => item.to === sendAddress)[0]?.to_memo;
  const curculateKrw = (
    selectedAsset?.tokenPrice
      ? selectedAsset?.tokenPrice * Number(sendAsset.replace(/,/g, ''))
      : selectedAsset?.coinPrice
        ? selectedAsset?.coinPrice * Number(sendAsset.replace(/,/g, ''))
        : 0
  ).toLocaleString();
  const isSendCheckBtnDisabled = sendAsset.length < 1;
  const fee = presentationStore.fee ?? 1;
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as {
    isSendView?: boolean;
    sendData?: {
      address: string;
      asset: string;
      selectedAsset?: ViewModel.IAsset;
      assetSymbol?: string;
    };
    userId?: string;
    userPhone?: string;
    from?: string;
    to?: string;
    amount?: number;
    memo?: string;
    viewType?: HomeViewType;
  };

  useEffect(() => {
    const checkAuth = async () => {
      await checkSessionAndRedirect();
    };

    checkAuth();
  }, []);


  useEffect(() => {
    const initializeData = async () => {
      try {
        if (userStore.user?.address) {
          const shouldFetchTransactions =
            activeTab === ViewModel.HomeTabType.ACTIVITIES ||
            viewType === HomeViewType.SOL ||
            viewType === HomeViewType.TOKEN;

          const results = await Promise.all([
            shouldFetchTransactions
              ? tryFetchAllTransactions(userStore.user.address)
              : Promise.resolve(null),
            activeTab === ViewModel.HomeTabType.ASSETS && viewType === ViewModel.HomeViewType.MAIN
              ? tryFetchBalance(userStore.user.address)
              : Promise.resolve(),
          ]);

          // 트랜잭션 조회 에러 처리
          const transactionResult = results[0];
          if (transactionResult && !transactionResult.succed && transactionResult.errorProcceded) {
            setTransactionErrorMessage(transactionResult.errorMessage || '거래 내역 조회에 실패했습니다.');
            setIsTransactionErrorModalOpen(true);
          }
        }
      } catch (error) {
        console.error('데이터 로드 실패:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [userStore.user?.address, userStore.user?.phone, activeTab, viewType]);

  useEffect(() => {
    setSelectedAsset(userStore.myAsset?.tokenAsset ?? userStore.user?.tokenAsset);
  }, [userStore.myAsset?.tokenAsset, userStore.user?.tokenAsset]);

  useEffect(() => {
    const handleSendView = () => {
      // 이미 처리했거나 isSendView가 없으면 실행하지 않음
      if (hasHandledSendView.current || !state?.isSendView) {
        return;
      }

      // 처리 시작 표시
      hasHandledSendView.current = true;

      setViewType(HomeViewType.SEND_CHECK);
      // 전송 완료 모달 표시
      setIsSendCompleteModalOpen(true);
    };

    handleSendView();
  }, [state]);

  // state가 변경되면 ref 초기화 (새로운 보내기 플로우 시작 시)
  useEffect(() => {
    if (!state?.isSendView) {
      hasHandledSendView.current = false;
    }
  }, [state?.isSendView]);

  useEffect(() => {
    if (state?.viewType === HomeViewType.SEND) {
      setViewType(HomeViewType.SEND);
      if (state.sendData) {
        setSendAddress(state.sendData.address);
      }
    }
  }, [state, userStore.myAsset]);

  /** trx 주소 복사 */
  const onClickCopyBtn = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const text = userStore.user?.address ?? '';

      navigator.clipboard.writeText(text);
    },
    [userStore.user?.address],
  );

  /** 잔액 재조회 */
  const onClickResetBtn = useCallback(async () => {
    const result = await tryFetchAllTransactions(userStore.user?.address ?? '');
    if (!result.succed && result.errorProcceded) {
      setTransactionErrorMessage(result.errorMessage || '거래 내역 조회에 실패했습니다.');
      setIsTransactionErrorModalOpen(true);
    }
  }, [userStore.user?.address]);

  /** 뷰 타입 변경 이벤트 */
  const onClickViewType = useCallback((viewType: HomeViewType) => {
    setViewType(viewType);
    window.scrollTo(0, 0);
  }, []);

  /** 뒤로가기 이벤트 */
  const onClickBackBtn = useCallback(() => {
    if (viewType === HomeViewType.SEND) {
      setSendAddress('');
      setSendAsset('');
      setMemo('');
    }
    setViewType(HomeViewType.MAIN);
  }, [viewType]);

  /** 보내기 이벤트 */
  const onClickSendBtn = useCallback(() => {
    setViewType(HomeViewType.SEND);
    setSelectedAsset(userStore.myAsset?.tokenAsset);
  }, [userStore.myAsset]);

  /** input - sendAddress 경 이벤트 */
  const onChangeSendAddress = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setSendAddress(value);
  }, []);

  /** input - sendAsset 변경 이벤트 */
  const onChangeSelectedAsset = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      // 빈 값이면 허용
      if (value === '') {
        setSendAsset('');
        return;
      }
      // 숫자와 소수점만 허용하고, 소수점 6째자리까지만 허용하는 정규 표현식
      // 한글, 영문 등 다른 문자는 모두 차단
      const regex = /^[0-9]*\.?[0-9]{0,6}$/;
      if (regex.test(value)) {
        setSendAsset(value);
      } else {
        // 잘못된 값이면 이전 값으로 복원
        e.currentTarget.value = sendAsset;
      }
    },
    [sendAsset],
  );

  const onKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // 숫자(0-9)와 소수점(.)만 허용
    const allowedKeys = /^[0-9.]$/;
    if (!allowedKeys.test(e.key)) {
      e.preventDefault();
      return;
    }

    const input = e.target as HTMLInputElement;
    const currentValue = input.value;
    const newValue = currentValue + e.key;

    // 소수점이 이미 있으면 또 입력 불가
    if (e.key === '.' && currentValue.includes('.')) {
      e.preventDefault();
      return;
    }

    // 소수점 6째자리까지만 허용
    const regex = /^\d*\.?\d{0,6}$/;
    if (!regex.test(newValue)) {
      e.preventDefault();
    }
  }, []);

  /** 주소 삭제 이벤트 */
  const onClickDeleteBtn = useCallback(() => {
    setSendAddress('');
    setSendAsset('');
    setSelectedAsset(userStore.myAsset?.tokenAsset);
  }, [userStore.myAsset?.tokenAsset]);

  /** 금액 초기화 이벤트 */
  const onClickResetAmountBtn = useCallback(() => setSendAsset(''), []);

  /** 붙여넣기 이벤트 */
  const onPaste = useCallback((text: string) => setSendAddress(text), []);

  /** 메모 설정 이벤트 (getMemo 결과 반영용) */
  const onSetMemo = useCallback((memoText: string) => {
    setMemo(memoText);
  }, []);

  /** 선택한 자산 변경 이벤트 */
  const onChangeSelectedAssetValue = useCallback(
    (assetType: ViewModel.AssetType) => {
      const newAsset =
        assetType === ViewModel.AssetType.COIN ? userStore.myAsset?.coinAsset : userStore.myAsset?.tokenAsset;
      setSelectedAsset(newAsset);
      setSendAsset('');
    },
    [userStore.myAsset],
  );

  /** 보내기 다음 버튼 체크 이벤트 */
  const onClickSendCheckBtn = useCallback(async () => {
    const isSuccessVerifyAddress = await tryVerifyAddress(sendAddress);

    if (!isSuccessVerifyAddress.succed) {
      setIsInvalidAddressModalOpen(true);
      return;
    }

    if ((selectedAsset?.balance ?? 0) < Number(sendAsset)) {
      setIsBalanceNotEnoughModalOpen(true);
      return;
    }
    tryFetchFee(userStore.user?.address ?? '', sendAddress, Number(sendAsset.replace(/,/g, '')));
    setViewType(HomeViewType.SEND_CHECK);
  }, [selectedAsset?.balance, sendAddress, sendAsset, userStore.user?.address]);

  /** 유효하지 않은 주소 모달 닫기 */
  const onClickInvalidAddressModalClose = useCallback(() => {
    setIsInvalidAddressModalOpen(false);
  }, []);

  /** 잔액 부족 모달 확인 */
  const onClickBalanceNotEnoughModalConfirm = useCallback(() => {
    setIsBalanceNotEnoughModalOpen(false);
    setSendAsset('');
  }, []);

  /** 트랜잭션 에러 모달 닫기 */
  const onClickTransactionErrorModalClose = useCallback(() => {
    setIsTransactionErrorModalOpen(false);
    setTransactionErrorMessage('');
  }, []);

  /** 클립보드 에러 - 모달 열기 */
  const onClipboardError = useCallback(() => {
    setIsClipboardErrorModalOpen(true);
  }, []);

  /** 클립보드 에러 모달 닫기 */
  const onClickClipboardErrorModalClose = useCallback(() => {
    setIsClipboardErrorModalOpen(false);
  }, []);

  /** 보내기 취소 이벤트 - 모달 열기 */
  const onClickSendCancelBtn = useCallback(() => {
    setIsCancelModalOpen(true);
  }, []);

  /** 취소 모달 닫기 */
  const onClickCancelModalClose = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);


  /** 취소 모달 - 예 */
  const onClickCancelModalYes = useCallback(() => {
    setIsCancelModalOpen(false);
    setViewType(HomeViewType.SEND);
    setSendAddress('');
    setMemo('');
  }, []);

  /** 전송 완료 모달 확인 */
  const onClickSendCompleteModalConfirm = useCallback(() => {
    setIsSendCompleteModalOpen(false);
    setViewType(HomeViewType.MAIN);
    // location state 초기화하여 새로고침 시 다시 팝업이 뜨지 않도록
    navigate(RouterPath.HOME, { replace: true, state: {} });
  }, [navigate]);

  /** 수수료 부족 모달 확인 */
  const onClickFeeNoticeModalClose = useCallback(() => {
    setIsFeeNoticeModalOpen(false);
  }, []);

  /** 보내기 확인 화면에서 뒤로가기 이벤트 */
  const onClickBacktoSendView = useCallback(() => setViewType(HomeViewType.SEND), []);
  /** 보내기 확인 화면에서 다음 버튼 이벤트 */
  const onClickSendCheckNextBtn = useCallback(() => {
    if ((presentationStore.balance?.coinBalance ?? 0) < MIN_SOL_BALANCE) {
      setIsFeeNotice(true);
      setIsFeeNoticeModalOpen(true);
    } else {
      navigate(RouterPath.PIN, {
        state: {
          type: 'send_verification',
          returnPath: RouterPath.HOME,
          isSendView: true,
          sendData: {
            address: sendAddress,
            asset: sendAsset,
            assetSymbol: selectedAsset?.symbol,
            assetName: selectedAsset?.name,
          },
          userId: userStore.user?.id,
          userPhone: userStore.user?.phone,
          from: userStore.user?.address,
          to: sendAddress,
          amount: Number(sendAsset),
          memo: memo.length < 1 ? defaultMemo : memo,
        },
        replace: true,
      });
    }
  }, [
    presentationStore.balance?.coinBalance,
    selectedAsset?.symbol,
    selectedAsset?.name,
    sendAddress,
    sendAsset,
    userStore.user?.id,
    userStore.user?.phone,
    userStore.user?.address,
    memo,
    defaultMemo,
    navigate,
  ]);

  /** 메인 뷰로 이동 이벤트 */
  const onMoveMainView = useCallback(() => {
    setViewType(HomeViewType.MAIN);
  }, []);

  /** 카메라 이벤트 */
  const onClickCameraBtn = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });

      // 비디오 요소 생성
      const video = document.createElement('video');
      video.srcObject = stream;
      video.style.position = 'fixed';
      video.style.top = '0';
      video.style.left = '0';
      video.style.width = '100vw';
      video.style.height = '100vh';
      video.style.objectFit = 'cover';
      video.style.zIndex = '9999';
      video.style.backgroundColor = '#000';

      // Canvas 요소 생성 (QR 스캔용)
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      // 전체 화면을 감싸는 컨테이너 생성
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100vw';
      container.style.height = '100vh';
      container.style.zIndex = '9999';
      container.style.cursor = 'pointer';

      // QR 스캔 프레임 생성
      const scanFrame = document.createElement('div');
      scanFrame.style.position = 'fixed';
      scanFrame.style.top = '50%';
      scanFrame.style.left = '50%';
      scanFrame.style.transform = 'translate(-50%, -50%)';
      scanFrame.style.width = '280px';
      scanFrame.style.height = '280px';
      scanFrame.style.border = '2px solid #fff';
      scanFrame.style.zIndex = '9999';
      scanFrame.style.pointerEvents = 'none'; // 프레임을 클릭해도 container의 클릭 이벤트가 발생하도록

      // 닫기 함수
      const closeCamera = () => {
        document.body.removeChild(container);
        stream.getTracks().forEach((track) => track.stop());
        clearInterval(scanInterval);
      };

      // 컨테이너 클릭 이벤트
      container.onclick = closeCamera;

      // QR 코드 스캔 함수
      const scanQRCode = () => {
        if (video.readyState === video.HAVE_ENOUGH_DATA && context) {
          canvas.height = video.videoHeight;
          canvas.width = video.videoWidth;
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height);

          if (code) {
            setSendAddress(code.data);
            closeCamera();
          }
        }
      };

      // 주기적으로 QR 스캔
      const scanInterval = setInterval(scanQRCode, 100);

      // 요소들을 컨테이너에 추가
      container.appendChild(video);
      container.appendChild(scanFrame);
      document.body.appendChild(container);
      video.play();
    } catch (err) {
      console.error('카메라 접근 실패:', err);
      alert('카메라 접근에 실패했습니다.');
    }
  }, [setSendAddress]);

  /** 선택한 자산 초기화 이벤트 */
  const onResetSelectedAsset = useCallback(() => {
    setSelectedAsset(userStore.myAsset?.tokenAsset);
  }, [userStore.myAsset]);

  /** 홈 탭 이벤트 */
  const onClickActiveTab = useCallback((tab: ViewModel.HomeTabType) => setActiveTab(tab), []);

  /** 이전 거래 보기 이벤트 */
  const onClickPrevTransactionBtn = useCallback(() => {
    const baseUrl = import.meta.env.VITE_SOLSCAN_EXPLORER_URL || 'https://solscan.io';
    const cluster = import.meta.env.VITE_SOLSCAN_CLUSTER || 'devnet';
    window.open(`${baseUrl}/account/${userStore.user?.address}?cluster=${cluster}`, '_blank');
  }, [userStore.user?.address]);

  /** 메모 변경 이벤트 */
  const onChangeMemo = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      if (memo.length === 0) setMemo('');
      if (value.length <= MAX_MEMO_LENGTH) setMemo(value);
      if (value.length < 1 && defaultMemo.length !== 0) setMemo(defaultMemo);
    },
    [defaultMemo, memo.length],
  );

  const resetSendInfo = useCallback(() => {
    setSendAsset('');
    setSendAddress('');
  }, []);

  /** viewType이 SEND가 아닐 때 memo 초기화 */
  useEffect(() => {
    if (viewType !== HomeViewType.SEND && viewType !== HomeViewType.SEND_CHECK) {
      setMemo('');
    }
  }, [viewType]);

  useEffect(() => {
    const handlePopState = () => {
      if (viewType === HomeViewType.SEND_CHECK) {
        setViewType(HomeViewType.MAIN); // 메인으로 이동
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [viewType]);



  return {
    isLoading,
    viewType,
    activities,
    isNotActivities,
    isWritting,
    sendAddress,
    sendAsset,
    curculateKrw,
    selectedAsset,
    trxAsset: userStore.myAsset?.coinAsset,
    tokenAsset: userStore.user?.tokenAsset,
    totalBalance:
      (Number(presentationStore.balance?.tokenKRW) + Number(presentationStore.balance?.coinKRW)).toLocaleString() ?? '',
    trxAddress: userStore.user?.address ?? '',
    trxTransactions: presentationStore.transactions.filter((item) => item.coinType === 'SOL'),
    // trxTransactions: presentationStore.transactions.filter((item) => item.coinType === ViewModel.AssetType.COIN),
    tokenTransactions: presentationStore.transactions.filter((item) => item.coinType === 'GDF'),
    // tokenTransactions: presentationStore.transactions.filter((item) => item.coinType === ViewModel.AssetType.TOKEN),
    trxBalance: (presentationStore.balance?.coinBalance ?? 0).toString(),
    trxKrw: Number(presentationStore.balance?.coinKRW ?? 0).toLocaleString() ?? 0,
    tokenBalance: (presentationStore.balance?.tokenBalance ?? 0).toString(),
    tokenKrw: Number(presentationStore.balance?.tokenKRW).toLocaleString() ?? 0,
    tokenPrice: presentationStore.balance?.tokenPrice ?? 0,
    trxPrice: presentationStore.balance?.coinPrice ?? 0,
    isSendCheckBtnDisabled,
    isFeeNotice,
    fee,
    coinType: presentationStore.transactions[0]?.coinType ?? '',
    activeTab,
    defaultMemo,
    memo,
    isSendingAsset,
    dbsBuyCount: Number(presentationStore.balance?.tokenBuyCount ?? 0).toLocaleString(),
    dbsBuyCountKRW: Number(presentationStore.balance?.tokenBuyCountKRW ?? 0).toLocaleString(),
    isCancelModalOpen,
    isSendCompleteModalOpen,
    onClickSendCompleteModalConfirm,
    isFeeNoticeModalOpen,
    onClickFeeNoticeModalClose,
    onClickActiveTab,
    onClickCopyBtn,
    onClickResetBtn,
    onClickViewType,
    onClickSendBtn,
    onClickBackBtn,
    onChangeSendAddress,
    onChangeSelectedAsset,
    onChangeSelectedAssetValue,
    onClickDeleteBtn,
    onClickResetAmountBtn,
    onPaste,
    onClickSendCheckBtn,
    isInvalidAddressModalOpen,
    onClickInvalidAddressModalClose,
    isBalanceNotEnoughModalOpen,
    onClickBalanceNotEnoughModalConfirm,
    isTransactionErrorModalOpen,
    transactionErrorMessage,
    onClickTransactionErrorModalClose,
    isClipboardErrorModalOpen,
    onClipboardError,
    onClickClipboardErrorModalClose,
    onClickSendCancelBtn,
    onClickCancelModalClose,
    onClickCancelModalYes,
    onClickBacktoSendView,
    onClickSendCheckNextBtn,
    onMoveMainView,
    onClickCameraBtn,
    onResetSelectedAsset,
    onClickPrevTransactionBtn,
    onChangeMemo,
    onKeyPress,
    resetSendInfo,
    onSetMemo,
  };
}
